import { useLazyQuery } from "@apollo/client";
import equal from "deep-equal";
import { ComponentProps, useCallback, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import Button from "@components/buttons/Button";
import styles from "@components/expert/ExpertConsultsPage.module.css";
import ExpertConsultsTable from "@components/expert/ExpertConsultsTable";
import ExpertConsultsFilters from "@components/forms/ExpertConsultsFilters";
import HandleQuery from "@components/HandleQuery";
import ExpertTablesPage from "@components/pageTypes/ExpertTablesPage";
import HugeText from "@components/text/HugeText";
import IconBase from "@components/text/IconBase";
import useSubscribeToJourneys from "@hooks/subscriptions/useSubscribeToJourneys";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import { NeedsInterpretationEnum } from "@typing/Enums";
import {
  ConsultReportStatusEnum,
  ConsultTimePeriodEnum,
  ExpertConsultsSortByEnum,
  ExpertGlobalRoleEnum,
  GetExpertConsultsDocument,
  GetExpertConsultsQuery,
  GetExpertConsultsQueryVariables,
  SortDirectionEnum
} from "@typing/Generated";

type Filters = ComponentProps<typeof ExpertConsultsFilters>["filters"];

const ExpertConsultsPage = () => {
  const myExpert = useMyExpert();
  const [sortBy, setSortBy] = useState<ExpertConsultsSortByEnum>(ExpertConsultsSortByEnum.DATE);
  const [sortDirection, setSortDirection] = useState<SortDirectionEnum>(SortDirectionEnum.DESCENDING);

  const [filters, setFilters] = useState<Filters>({
    consultReportStatus: ConsultReportStatusEnum.ALL,
    employerId: "",
    endTime: "",
    expertId: myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER ? null : myExpert?.id,
    needsInterpretation: NeedsInterpretationEnum.ALL,
    startTime: "",
    timePeriod: ConsultTimePeriodEnum.UPCOMING
  });

  const handleFilterChange = useCallback(
    (newFilters: Filters) => {
      if (!equal(newFilters, filters)) {
        if (newFilters.timePeriod !== filters.timePeriod && newFilters.timePeriod === ConsultTimePeriodEnum.UPCOMING) {
          setSortDirection(SortDirectionEnum.ASCENDING);
        }
        if (newFilters.timePeriod !== filters.timePeriod && newFilters.timePeriod === ConsultTimePeriodEnum.PAST) {
          setSortDirection(SortDirectionEnum.DESCENDING);
        }
        setFilters(newFilters);
      }
    },
    [filters]
  );

  const [getExpertConsults, getExpertConsultsQuery] = useLazyQuery<
    GetExpertConsultsQuery,
    GetExpertConsultsQueryVariables
  >(GetExpertConsultsDocument);

  useSubscribeToJourneys(() => {
    getExpertConsultsQuery.refetch();
  });

  const consults = useNodesFromConnection(getExpertConsultsQuery.data?.expertConsults);

  useEffect(() => {
    if (myExpert) {
      getExpertConsults({
        variables: {
          consultReportStatus: filters.consultReportStatus,
          duration: filters.duration,
          employerId: filters.employerId,
          endDate: filters.endTime ?? null,
          expertId: filters.expertId,
          needsInterpretation:
            filters.needsInterpretation === NeedsInterpretationEnum.NEEDS_INTERPRETATION ? true : false,
          selfPaid: filters.selfPaid,
          sortBy,
          sortDirection,
          startDate: filters.startTime ?? null,
          timePeriod: filters.timePeriod
        }
      });
    }
  }, [filters, getExpertConsults, myExpert, sortBy, sortDirection]);

  return (
    <ExpertTablesPage helmetKey="pageTitles.expert.consult.list">
      <div className={styles.goBack}>
        <Button fill="clear" routerLink={`/expert/dashboard?expertId=${filters.expertId ?? ""}`}>
          <IconBase icon="chevronLeft" slot="start" />
          <FormattedMessage id="pages.expert.consults.dashboard" />
        </Button>
      </div>

      <div className={styles.title}>
        <HugeText>
          <FormattedMessage id="pages.expert.consults.consults" />
        </HugeText>
      </div>
      <div className={styles.tableContainer}>
        <ExpertConsultsFilters filters={filters} onChange={handleFilterChange} />
      </div>
      <HandleQuery query={getExpertConsultsQuery}>
        {data => (
          <ExpertConsultsTable
            consults={consults}
            expertId={filters.expertId}
            fetchMore={getExpertConsultsQuery.fetchMore}
            pageInfo={data.expertConsults.pageInfo}
            setSortBy={setSortBy}
            setSortDirection={setSortDirection}
            sortBy={sortBy}
            sortDirection={sortDirection}
            totalCount={data.expertConsults.totalCount}
            upcoming={filters.timePeriod}
          />
        )}
      </HandleQuery>
    </ExpertTablesPage>
  );
};

export default ExpertConsultsPage;
